import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../api/client';
import type {
  SectorCoverageBootstrap,
  SectorCoverageCycle,
  SectorCoverageMatrix,
  SectorCoverageMyPreferences,
  SectorCoverageOpenCycleRequest,
  SectorCoverageOptimizeResponse,
  SectorCoveragePreferencesRequest,
} from '../types';

const KEYS = {
  bootstrap: ['sector-coverage', 'cycle'] as const,
  myPrefs: ['sector-coverage', 'my-preferences'] as const,
  matrix: ['sector-coverage', 'preferences'] as const,
};

export function useCoverageBootstrap() {
  return useQuery<SectorCoverageBootstrap>({
    queryKey: KEYS.bootstrap,
    queryFn: () => apiFetch<SectorCoverageBootstrap>('/api/sector-coverage/cycle'),
    staleTime: 60 * 1000,
  });
}

export function useMyPreferences(enabled: boolean) {
  return useQuery<SectorCoverageMyPreferences>({
    queryKey: KEYS.myPrefs,
    queryFn: () => apiFetch<SectorCoverageMyPreferences>('/api/sector-coverage/my-preferences'),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useSubmitPreferences() {
  const qc = useQueryClient();
  return useMutation<SectorCoverageMyPreferences, Error, SectorCoveragePreferencesRequest>({
    mutationFn: (body) =>
      apiFetch<SectorCoverageMyPreferences>('/api/sector-coverage/my-preferences', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.myPrefs });
      qc.invalidateQueries({ queryKey: KEYS.matrix });
    },
  });
}

export function useWithdrawPreferences() {
  const qc = useQueryClient();
  return useMutation<{ status: string }>({
    mutationFn: () =>
      apiFetch<{ status: string }>('/api/sector-coverage/my-preferences', { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.myPrefs });
      qc.invalidateQueries({ queryKey: KEYS.matrix });
    },
  });
}

export function usePreferenceMatrix(enabled: boolean) {
  return useQuery<SectorCoverageMatrix>({
    queryKey: KEYS.matrix,
    queryFn: () => apiFetch<SectorCoverageMatrix>('/api/sector-coverage/preferences'),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useRunOptimizer() {
  return useMutation<SectorCoverageOptimizeResponse, Error, { maxPerUser?: number | null }>({
    mutationFn: (vars) => {
      const qs =
        vars.maxPerUser != null ? `?max_per_user=${encodeURIComponent(vars.maxPerUser)}` : '';
      return apiFetch<SectorCoverageOptimizeResponse>(
        `/api/sector-coverage/optimize${qs}`,
        { method: 'POST' },
      );
    },
  });
}

export function useOpenCycle() {
  const qc = useQueryClient();
  return useMutation<SectorCoverageCycle, Error, SectorCoverageOpenCycleRequest>({
    mutationFn: (body) =>
      apiFetch<SectorCoverageCycle>('/api/sector-coverage/cycles', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.bootstrap });
      qc.invalidateQueries({ queryKey: KEYS.myPrefs });
      qc.invalidateQueries({ queryKey: KEYS.matrix });
    },
  });
}

export function useCloseCycle() {
  const qc = useQueryClient();
  return useMutation<SectorCoverageCycle, Error, number>({
    mutationFn: (cycleId) =>
      apiFetch<SectorCoverageCycle>(`/api/sector-coverage/cycles/${cycleId}`, {
        method: 'PATCH',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.bootstrap });
      qc.invalidateQueries({ queryKey: KEYS.matrix });
    },
  });
}
